import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import type { MarketplaceOrderListItemDto } from '@foodfluence/shared';
import { InfluencerDataContext } from './InfluencerDataContext';
import { lcApi } from '../services/api/lc';

const BACKGROUND_REFETCH_MIN_MS = 30_000;

export type MarketplaceOrdersContextValue = {
  orders: MarketplaceOrderListItemDto[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

const defaultValue: MarketplaceOrdersContextValue = {
  orders: [],
  loading: false,
  error: null,
  refetch: async () => {},
};

export const MarketplaceOrdersContext = createContext<MarketplaceOrdersContextValue>(defaultValue);

export function useMarketplaceOrders(): MarketplaceOrdersContextValue {
  return useContext(MarketplaceOrdersContext);
}

export function MarketplaceOrdersProvider({ children }: { children: ReactNode }) {
  const { influencerData } = useContext(InfluencerDataContext);
  const influencerId = influencerData?.id;

  const [orders, setOrders] = useState<MarketplaceOrderListItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastFetchAtRef = useRef(0);
  const inFlightRef = useRef<Promise<void> | null>(null);

  const fetchOrders = useCallback(
    async (options?: { force?: boolean }) => {
      if (!influencerId) {
        setOrders([]);
        setLoading(false);
        setError(null);
        return;
      }

      if (!options?.force) {
        const elapsed = Date.now() - lastFetchAtRef.current;
        if (elapsed < BACKGROUND_REFETCH_MIN_MS) {
          return;
        }
      }

      if (inFlightRef.current) {
        return inFlightRef.current;
      }

      const run = (async () => {
        setLoading(true);
        setError(null);
        try {
          const res = await lcApi.listMarketplaceOrders();
          setOrders(res.data ?? []);
          lastFetchAtRef.current = Date.now();
        } catch (e) {
          console.error('Error fetching marketplace orders', e);
          setError('Could not load marketplace orders.');
        } finally {
          setLoading(false);
          inFlightRef.current = null;
        }
      })();

      inFlightRef.current = run;
      return run;
    },
    [influencerId],
  );

  const refetch = useCallback(async () => {
    await fetchOrders({ force: true });
  }, [fetchOrders]);

  useEffect(() => {
    if (!influencerId) {
      setOrders([]);
      setLoading(false);
      setError(null);
      lastFetchAtRef.current = 0;
      return;
    }

    void fetchOrders({ force: true });
  }, [fetchOrders, influencerId]);

  useEffect(() => {
    if (!influencerId) {
      return;
    }

    const refreshIfVisible = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      void fetchOrders();
    };

    document.addEventListener('visibilitychange', refreshIfVisible);
    window.addEventListener('focus', refreshIfVisible);

    return () => {
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.removeEventListener('focus', refreshIfVisible);
    };
  }, [fetchOrders, influencerId]);

  const value: MarketplaceOrdersContextValue = { orders, loading, error, refetch };

  return (
    <MarketplaceOrdersContext.Provider value={value}>{children}</MarketplaceOrdersContext.Provider>
  );
}
